import {z} from 'zod';
import {
  extensionKeySchema,
  pluginConfigSchema,
  secondsOrMsSchema,
  type JsonRecord,
} from '../shared';
import {
  targetTupleSchema,
  targetObjectSchema,
  targetTransforms,
  TargetDefinition,
  TargetObject,
} from '../target';
import {scrollTriggerConfigSchema} from './scroll';
import {
  standardPlaybackControlSchema,
  timelineControlTypeSchema,
} from './control';
import {
  triggerConditionalLogicTupleSchema,
  triggerConditionalLogicObjectSchema,
  conditionTransforms,
} from './condition';

/**
 * Storage bounds for a trigger's `assignedInstances`, shared with the server
 * and multiplayer sanitizers so a bump here cannot leave them behind.
 */
export const MAX_ASSIGNED_INSTANCES = 20;
export const MAX_ASSIGNED_INSTANCE_ID_LENGTH = 64;

const baseTriggerConfig = {
  delay: secondsOrMsSchema.optional(),
  control: standardPlaybackControlSchema.optional(),
  jump: secondsOrMsSchema.optional(),
  speed: z.number().nonnegative().optional(),
  controlType: timelineControlTypeSchema.optional(),
  scrollTriggerConfig: scrollTriggerConfigSchema.optional(),
  pluginConfig: pluginConfigSchema.optional(),
  /**
   * Id of the user-managed timeline group this trigger should control.
   * Routes the trigger to the timeline whose `timeline.groupId` matches.
   * Optional - undefined means the trigger plays every timeline in the
   * interaction (default for single-timeline trigger types). Null means the
   * trigger is explicitly unassigned and should not play any group.
   *
   * Bounded and non-empty (mirroring `assignedTimelineRole`): an empty string
   * would be classified as "routed" by sharedTimelineAnalysis (`!== undefined`)
   * yet dispatch through the play-all path in StandardTriggerStrategy (falsy),
   * so it's rejected here to keep those predicates aligned and to keep
   * unbounded ids out of stored/published IX3 data.
   */
  assignedGroupId: z.string().min(1).max(64).nullable().optional(),
  // Permissive on the role *name* so core can route plugin-owned timeline
  // roles without knowing each plugin's role names, but non-empty: an empty
  // string would be classified as "routed" by sharedTimelineAnalysis yet
  // dispatch through the play-all path at runtime (truthy check), so reject it
  // to keep those predicates aligned (mirrors `assignedGroupId` above).
  assignedTimelineRole: z.string().min(1).max(64).optional(),
  /**
   * The popovers this trigger drives, as `[componentId, elementId]` paths.
   *
   * Only meaningful on a popover interaction, and only on a trigger that is not
   * itself a `wf:popover` — a popover trigger already IS one popover. Follows the
   * `assignedGroupId` convention: `undefined` means every popover the interaction
   * names, so a popover added later is picked up without revisiting each trigger;
   * an empty array means the author explicitly narrowed it to none.
   *
   * Bounded by the same ceiling as triggers per interaction, since each popover
   * needs its own `wf:popover` trigger to be nameable here.
   */
  assignedInstances: z
    .array(
      z.tuple([
        z.string().min(1).max(MAX_ASSIGNED_INSTANCE_ID_LENGTH),
        z.string().min(1).max(MAX_ASSIGNED_INSTANCE_ID_LENGTH),
      ])
    )
    .max(MAX_ASSIGNED_INSTANCES)
    .optional(),
};

export const triggerConfigSchema = z.object({
  ...baseTriggerConfig,
  conditionalLogic: triggerConditionalLogicTupleSchema.optional(),
});
export type BaseTriggerConfig<TConfig = JsonRecord> = Omit<
  z.infer<typeof triggerConfigSchema>,
  'pluginConfig'
> & {
  pluginConfig?: TConfig;
};
export type TriggerConfig<TConfig = JsonRecord> = BaseTriggerConfig<TConfig>;

export const triggerTupleSchema = z.union([
  z.tuple([
    /** Extension key for the trigger e.g. "wf:click" */
    extensionKeySchema,
    /** Configuration for the trigger */
    triggerConfigSchema,
    /** Target definition for the trigger */
    targetTupleSchema,
  ]),
  z.tuple([extensionKeySchema, triggerConfigSchema]),
]);
export type TriggerTuple<TConfig = JsonRecord, TTarget = unknown> =
  | [
      z.infer<typeof extensionKeySchema>,
      TriggerConfig<TConfig>,
      TargetDefinition<TTarget>,
    ]
  | [z.infer<typeof extensionKeySchema>, TriggerConfig<TConfig>];
export type Trigger<TConfig = JsonRecord, TTarget = unknown> = TriggerTuple<
  TConfig,
  TTarget
>;

export const triggerObjectSchema = z.object({
  /** Extension key for the trigger e.g. "wf:click" */
  extensionKey: extensionKeySchema,
  /** Configuration for the trigger */
  config: z.object({
    ...baseTriggerConfig,
    conditionalLogic: triggerConditionalLogicObjectSchema.optional(),
  }),
  /** Target definition for the trigger */
  target: targetObjectSchema.optional(),
});
export type TriggerObjectConfig<TConfig = JsonRecord> = Omit<
  z.infer<typeof triggerObjectSchema>['config'],
  'pluginConfig'
> & {
  pluginConfig?: TConfig;
};

export type TriggerObject<TConfig = JsonRecord, TTarget = unknown> = {
  extensionKey: z.infer<typeof extensionKeySchema>;
  config: TriggerObjectConfig<TConfig>;
  target?: TargetObject<TTarget>;
};

export const triggerTransforms = {
  tupleToObject: <TConfig = JsonRecord, TTarget = unknown>(
    tuple: TriggerTuple<TConfig, TTarget>
  ): TriggerObject<TConfig, TTarget> => {
    const [extensionKey, config, target] = tuple;
    const {conditionalLogic, ...restConfig} = config;

    const trigger: TriggerObject<TConfig, TTarget> = {
      extensionKey,
      config: {
        ...restConfig,
        ...(conditionalLogic && {
          conditionalLogic: {
            ...conditionalLogic,
            conditions: conditionalLogic.conditions.map((c) =>
              conditionTransforms.tupleToObject(c)
            ),
          },
        }),
      },
      ...(target && {
        target: targetTransforms.tupleToObject(target),
      }),
    };
    return trigger;
  },

  objectToTuple: <TConfig = JsonRecord, TTarget = unknown>(
    obj: TriggerObject<TConfig, TTarget>
  ): TriggerTuple<TConfig, TTarget> => {
    const {
      delay,
      control,
      jump,
      speed,
      controlType,
      scrollTriggerConfig,
      conditionalLogic,
      pluginConfig,
      assignedGroupId,
      assignedTimelineRole,
      assignedInstances,
    } = obj.config;

    const triggerConfig = {
      ...(delay !== undefined && {delay}),
      ...(control !== undefined && {control}),
      ...(jump !== undefined && {jump}),
      ...(speed !== undefined && {speed}),
      ...(controlType !== undefined && {controlType}),
      ...(scrollTriggerConfig !== undefined && {scrollTriggerConfig}),
      ...(assignedTimelineRole !== undefined && {assignedTimelineRole}),
      ...(conditionalLogic
        ? {
            conditionalLogic: {
              conditions: conditionalLogic.conditions.map((c) =>
                conditionTransforms.objectToTuple({
                  ...c,
                  value: c.value as unknown,
                })
              ),
              ifTrue: conditionalLogic.ifTrue,
              ifFalse: conditionalLogic.ifFalse,
            },
          }
        : {}),
      ...(pluginConfig !== undefined && {pluginConfig}),
      ...(assignedGroupId !== undefined && {assignedGroupId}),
      ...(assignedInstances !== undefined && {assignedInstances}),
    };

    if (obj.target) {
      return [
        obj.extensionKey,
        triggerConfig,
        targetTransforms.objectToTuple(obj.target),
      ];
    }

    return [obj.extensionKey, triggerConfig];
  },
};
